// Geometry (§6.2 pass 17, §11.3). Pure math, shared by the GPU warp pass, overlays and tests.
//
// Model. The source is W×H pixels (already oriented). The "frame" has the same size; its content is the
// source, keystoned (perspective homography P) and then rotated by `angle` degrees clockwise, both
// about the centre. The crop rect {x,y,w,h} is normalized in frame coordinates (0..1, y down).
// Output pixel (ox, oy) of an Wout×Hout output maps to a source-normalized point via one 3×3 matrix:
//   src = Nsrc · P⁻¹ · R(−angle) · C · A · (ox, oy, 1)ᵀ      (then divide by z)
// Points outside [0,1]² have no source (alpha 0, §11.3).
import type { Settings } from '../model/types';

export type M3 = [number, number, number, number, number, number, number, number, number]; // row-major
export type Rect = { x: number; y: number; w: number; h: number };

export const mul3 = (a: M3, b: M3): M3 => {
  const o = new Array<number>(9) as M3;
  for (let r = 0; r < 3; r++)
    for (let c = 0; c < 3; c++)
      o[r * 3 + c] = a[r * 3]! * b[c]! + a[r * 3 + 1]! * b[3 + c]! + a[r * 3 + 2]! * b[6 + c]!;
  return o;
};
export function inv3(m: M3): M3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h,
    B = -(d * i - f * g),
    C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-18) throw new Error('singular geometry matrix');
  const k = 1 / det;
  return [
    A * k,
    -(b * i - c * h) * k,
    (b * f - c * e) * k,
    B * k,
    (a * i - c * g) * k,
    -(a * f - c * d) * k,
    C * k,
    -(a * h - b * g) * k,
    (a * e - b * d) * k,
  ];
}
export const apply3 = (m: M3, x: number, y: number): [number, number] => {
  const z = m[6] * x + m[7] * y + m[8];
  return [(m[0] * x + m[1] * y + m[2]) / z, (m[3] * x + m[4] * y + m[5]) / z];
};
export const I3: M3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];
const translate = (tx: number, ty: number): M3 => [1, 0, tx, 0, 1, ty, 0, 0, 1];
const scale = (sx: number, sy: number): M3 => [sx, 0, 0, 0, sy, 0, 0, 0, 1];
const rotate = (deg: number): M3 => {
  const r = (deg * Math.PI) / 180,
    c = Math.cos(r),
    s = Math.sin(r);
  return [c, -s, 0, s, c, 0, 0, 0, 1];
};
/** Column-major Float32Array for `uniformMatrix3fv(…, false, …)`. */
export const toGl3 = (m: M3) => new Float32Array([m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]);

/** Homography mapping 4 source points to 4 destination points (DLT, h33 = 1). */
export function homographyFromQuad(src: [number, number][], dst: [number, number][]): M3 {
  const A: number[][] = [];
  const bvec: number[] = [];
  for (let k = 0; k < 4; k++) {
    const [x, y] = src[k]!;
    const [u, v] = dst[k]!;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    bvec.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    bvec.push(v);
  }
  // Gaussian elimination with partial pivoting on the 8×8 system.
  const n = 8;
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(A[r]![col]!) > Math.abs(A[piv]![col]!)) piv = r;
    [A[col], A[piv]] = [A[piv]!, A[col]!];
    [bvec[col], bvec[piv]] = [bvec[piv]!, bvec[col]!];
    for (let r = col + 1; r < n; r++) {
      const f = A[r]![col]! / A[col]![col]!;
      for (let c = col; c < n; c++) A[r]![c]! -= f * A[col]![c]!;
      bvec[r]! -= f * bvec[col]!;
    }
  }
  const h = new Array<number>(n).fill(0);
  for (let r = n - 1; r >= 0; r--) {
    let s = bvec[r]!;
    for (let c = r + 1; c < n; c++) s -= A[r]![c]! * h[c]!;
    h[r] = s / A[r]![r]!;
  }
  return [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!, 1];
}

/**
 * Keystone homography in centred pixel space (§11.3). Vertical v > 0 scales the TOP edge by
 * 1 − v/100·0.4 (v < 0 scales the bottom edge instead: symmetric); horizontal likewise for the right
 * (h > 0) / left (h < 0) edge's height. Maps source → keystoned.
 */
export function keystone(vertical: number, horizontal: number, W: number, H: number): M3 {
  if (vertical === 0 && horizontal === 0) return I3;
  const hw = W / 2,
    hh = H / 2;
  // corners: TL, TR, BR, BL
  const src: [number, number][] = [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ];
  const dst = src.map(([x, y]) => [x, y] as [number, number]);
  const kv = 1 - (Math.abs(vertical) / 100) * 0.4;
  if (vertical > 0) {
    dst[0]![0] *= kv;
    dst[1]![0] *= kv;
  }
  if (vertical < 0) {
    dst[2]![0] *= kv;
    dst[3]![0] *= kv;
  }
  const kh = 1 - (Math.abs(horizontal) / 100) * 0.4;
  if (horizontal > 0) {
    dst[1]![1] *= kh;
    dst[2]![1] *= kh;
  }
  if (horizontal < 0) {
    dst[0]![1] *= kh;
    dst[3]![1] *= kh;
  }
  return homographyFromQuad(src, dst);
}

export type GeometryInput = Pick<Settings['geometry'], 'crop' | 'angle' | 'vertical' | 'horizontal'>;

/** Frame-normalized point → source-normalized point. */
export function frameToSource(g: GeometryInput, W: number, H: number): M3 {
  const C = mul3(scale(W, H), translate(-0.5, -0.5)); // frame normalized → centred pixels
  const Rinv = rotate(-g.angle);
  const Pinv = inv3(keystone(g.vertical, g.horizontal, W, H));
  const N = mul3(translate(0.5, 0.5), scale(1 / W, 1 / H)); // centred pixels → source normalized
  return mul3(N, mul3(Pinv, mul3(Rinv, C)));
}

/** Output pixel → source-normalized, for an output of Wout×Hout covering the crop. */
export function outputToSource(g: GeometryInput, W: number, H: number, Wout: number, Hout: number): M3 {
  const A = mul3(translate(g.crop.x, g.crop.y), scale(g.crop.w / Wout, g.crop.h / Hout));
  return mul3(frameToSource(g, W, H), A);
}

/** Output size in pixels at full resolution (crop in source pixels). */
export function fullOutputSize(g: GeometryInput, W: number, H: number): { width: number; height: number } {
  return { width: Math.max(1, Math.round(g.crop.w * W)), height: Math.max(1, Math.round(g.crop.h * H)) };
}

const inside = (p: [number, number], eps = 1e-9) =>
  p[0] >= -eps && p[0] <= 1 + eps && p[1] >= -eps && p[1] <= 1 + eps;

/** True when every point of the crop maps inside the source (no empty corners). */
export function cropIsCovered(g: GeometryInput, W: number, H: number): boolean {
  const M = frameToSource(g, W, H);
  const { x, y, w, h } = g.crop;
  // Edges of a convex quad map to straight lines under a homography; corners suffice.
  return [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ].every(([a, b]) => inside(apply3(M, a!, b!)));
}

/** Constrain (§11.3): shrinks the crop about its centre (keeping its aspect) until no empty corner shows. */
export function constrainCrop(g: GeometryInput, W: number, H: number): Rect {
  if (cropIsCovered(g, W, H)) return g.crop;
  const cx = g.crop.x + g.crop.w / 2,
    cy = g.crop.y + g.crop.h / 2;
  const at = (s: number): Rect => ({
    x: cx - (g.crop.w * s) / 2,
    y: cy - (g.crop.h * s) / 2,
    w: g.crop.w * s,
    h: g.crop.h * s,
  });
  let lo = 0,
    hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (cropIsCovered({ ...g, crop: at(mid) }, W, H)) lo = mid;
    else hi = mid;
  }
  return at(lo);
}

/** Largest crop of pixel aspect `aspect` (w/h) centred in the frame. */
export function fitAspect(aspect: number, W: number, H: number): Rect {
  const frameAspect = W / H;
  if (aspect >= frameAspect) {
    const h = frameAspect / aspect;
    return { x: 0, y: (1 - h) / 2, w: 1, h };
  }
  const w = aspect / frameAspect;
  return { x: (1 - w) / 2, y: 0, w, h: 1 };
}

export const ASPECTS: Record<string, number | null> = {
  free: null,
  original: null,
  '1:1': 1,
  '4:5': 4 / 5,
  '3:2': 3 / 2,
  '2:3': 2 / 3,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '4:3': 4 / 3,
};

/**
 * Straighten (§11.3): the rotation that makes the drawn line horizontal (or vertical, whichever it is
 * closer to). Points are in frame pixels. Returns the new absolute angle, clamped to ±45.
 */
export function straightenAngle(current: number, x0: number, y0: number, x1: number, y1: number): number {
  const a = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI; // −180..180, clockwise positive (y down)
  let delta = -a;
  // Bring to the nearest of 0, ±90, ±180.
  delta = ((delta % 90) + 90) % 90;
  if (delta > 45) delta -= 90;
  return Math.max(-45, Math.min(45, current + delta));
}
